import React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '../../ui/Checkbox/Checkbox';
import { LinkButton } from '../../ui/LinkButton/LinkButton';
import { TranslationKey } from '../../i18n/translationKeys';
import type { RelanceOnboardingConsentProps } from './types';

/**
 * The Loi 25 consent block — legal copy, the mandatory consent checkbox, and (when applicable) the
 * "Je préfère refuser" link. Shared by the onboarding walkthrough's screen 3 and the Settings
 * re-consent flow (after a prior refusal), so the same provisional legal text appears in both places
 * rather than drifting apart.
 */
export const RelanceOnboardingConsent: React.FC<RelanceOnboardingConsentProps> = ({
    checked, onCheckedChange, onRefuseClick,
}) => {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col gap-l w-full max-w-md">
            <div className="bg-surface-secondary rounded-lg p-l text-left">
                <p className="text-s text-content-secondary">{t(TranslationKey.Onboarding.Consent.LegalText)}</p>
                {/* Provisional legal copy, flagged in the PR as pending compliance/legal review. */}
                <p data-testid="onboarding-consent-draft-flag" className="text-s text-danger mt-xs">
                    *{t(TranslationKey.Onboarding.DraftTextFlag)}
                </p>
            </div>

            <Checkbox
                data-testid="onboarding-consent-checkbox"
                checked={checked}
                onChange={(e) => onCheckedChange(e.target.checked)}
                label={t(TranslationKey.Onboarding.Consent.CheckboxLabel)}
            />

            {onRefuseClick && (
                <LinkButton data-testid="onboarding-refuse-link" onClick={onRefuseClick} className="self-start">
                    {t(TranslationKey.Onboarding.Consent.RefuseLink)}
                </LinkButton>
            )}
        </div>
    );
};
